import * as THREE from 'three';
import type { Input } from '../core/Input';
import type { AudioManager } from '../audio/AudioManager';
import type { PlayerController } from '../player/PlayerController';

const INTERACT_DIST = 1.85;
const FACING_DOT = 0.15;
const TAP_AUTO_OFF = 8;

interface SinkRuntime {
  root: THREE.Object3D;
  stream: THREE.Object3D;
  puddle: THREE.Object3D;
  knobs: THREE.Object3D[];
  on: boolean;
  onT: number;
}

/** F-to-toggle bathroom taps. Stream meshes live on the sink group. */
export class SinkSystem {
  private readonly sinks: SinkRuntime[] = [];
  private readonly forward = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly projected = new THREE.Vector3();
  private stopLoop: (() => void) | null = null;

  constructor(
    scene: THREE.Object3D,
    private readonly audio: AudioManager,
  ) {
    scene.traverse((o) => {
      if (o.userData?.interact !== 'sink') return;
      const stream = o.userData.stream as THREE.Object3D | undefined;
      const puddle = o.userData.puddle as THREE.Object3D | undefined;
      if (!stream || !puddle) return;
      stream.visible = false;
      puddle.visible = false;
      this.sinks.push({
        root: o,
        stream,
        puddle,
        knobs: (o.userData.knobs as THREE.Object3D[] | undefined) ?? [],
        on: false,
        onT: 0,
      });
    });
  }

  /** Returns true if F was used on a tap this frame. */
  update(player: PlayerController, input: Input, dt: number, allowInteract = true): boolean {
    for (const s of this.sinks) {
      const mat = (s.stream as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (s.on && mat?.map) {
        mat.map.offset.y = (mat.map.offset.y + dt * 1.6) % 1;
        mat.map.needsUpdate = true;
      }
      if (s.on) {
        s.onT -= dt;
        if (s.onT <= 0) this.setOn(s, false);
      }
    }

    if (!allowInteract || !input.interactPressed) return false;
    const best = this.pick(player);
    if (!best) return false;
    this.setOn(best, !best.on);
    return true;
  }

  lookTip(
    player: PlayerController,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): { label: string; x: number; y: number } | null {
    const s = this.pick(player);
    if (!s || width < 8 || height < 8) return null;
    s.root.getWorldPosition(this.projected);
    this.projected.y += 0.95;
    this.projected.project(camera);
    if (this.projected.z < -1 || this.projected.z > 1) return null;
    return {
      label: s.on ? 'F - Turn off tap' : 'F - Turn on tap',
      x: Math.min(width - 40, Math.max(40, (this.projected.x * 0.5 + 0.5) * width)),
      y: Math.min(height - 24, Math.max(22, (-this.projected.y * 0.5 + 0.5) * height)),
    };
  }

  private setOn(s: SinkRuntime, on: boolean): void {
    s.on = on;
    s.onT = on ? TAP_AUTO_OFF : 0;
    s.stream.visible = on;
    s.puddle.visible = on;
    for (const knob of s.knobs) knob.rotation.z = on ? 0.7 : 0;

    const anyOn = this.sinks.some((x) => x.on);
    this.stopLoop?.();
    this.stopLoop = null;
    if (anyOn) {
      const src = this.sinks.find((x) => x.on) ?? s;
      src.root.getWorldPosition(this.to);
      this.to.y += 0.9;
      this.stopLoop = this.audio.playLoopAt('waterLeak', this.to, {
        volume: 0.12,
        rate: 1.35,
        range: 10,
        refDistance: 1.6,
      });
    }
  }

  private pick(player: PlayerController): SinkRuntime | null {
    const pos = player.body.position;
    this.forward.set(-Math.sin(player.yaw), 0, -Math.cos(player.yaw));
    let best: SinkRuntime | null = null;
    let bestScore = Infinity;
    for (const s of this.sinks) {
      s.root.getWorldPosition(this.to);
      if (Math.abs(this.to.y - pos.y) > 1.5) continue;
      this.to.y = pos.y;
      const dist = this.to.distanceTo(pos);
      if (dist > INTERACT_DIST) continue;
      this.to.sub(pos);
      if (this.to.lengthSq() > 1e-6) this.to.normalize();
      const facing = this.forward.dot(this.to);
      if (facing < FACING_DOT && dist > 0.9) continue;
      const score = dist - facing * 0.35;
      if (score < bestScore) {
        bestScore = score;
        best = s;
      }
    }
    return best;
  }
}
